import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import Navbar from "./components/Navbar";
import HeroSection from "./components/HeroSection";
import AboutSection from "./components/AboutSection";
import ServicesSection from "./components/ServicesSection";
import HowItWorksSection from "./components/HowItWorksSection";
import IndustriesSection from "./components/IndustriesSection";
import TestimonialsSection from "./components/TestimonialsSection";
import FAQSection from "./components/FAQSection";
import ContactSection from "./components/ContactSection";
import Footer from "./components/Footer";
import MarqueeBand from "./components/MarqueeBand";
import CareersPage from "./pages/CareersPage";
import ResourcesPage from "./pages/ResourcesPage";
import AdminPage from "./pages/AdminPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import CookiesPage from "./pages/CookiesPage";
import AccessibilityPage from "./pages/AccessibilityPage";
import IndustryPage from "./pages/IndustryPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import ChatWidget from "./components/ChatWidget";
import CookieConsent from "./components/CookieConsent";
import SmoothScroll from "./components/SmoothScroll";
import PageTransition from "./components/PageTransition";
import SEO from "./components/SEO";

const homepageJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Agentic Atoms",
    "url": "https://agenticatoms.com",
    "logo": "https://customer-assets.emergentagent.com/job_stylish-agentic/artifacts/lev4hvvo_image.png",
    "description": "Agentic Atoms builds custom AI chatbots, AI-powered websites, AI voice agents, AI receptionists, and AI sales agents for small and mid-sized businesses. We design, build, and deploy working AI systems in 30 days.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "5830 E 2nd St, Ste 7000 #30038",
      "addressLocality": "Casper",
      "addressRegion": "WY",
      "postalCode": "82609",
      "addressCountry": "US"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "connect@agenticatoms.com",
      "telephone": "+1-227-241-6372",
      "contactType": "sales"
    },
    "sameAs": [
      "https://www.linkedin.com/company/agentic-atoms/",
      "https://www.instagram.com/agenticatoms/"
    ],
    "knowsAbout": [
      "AI Chatbot Builder",
      "AI Website Builder",
      "AI Voice Agent",
      "AI Receptionist",
      "AI Sales Agent",
      "Custom AI Applications",
      "Workflow Automation",
      "AI for Local Business"
    ]
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Agentic Atoms",
    "url": "https://agenticatoms.com",
    "description": "Build custom AI chatbots, AI websites, AI voice agents, and AI sales agents for your business. Working AI systems deployed in 30 days.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://agenticatoms.com/?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Agentic Atoms",
    "url": "https://agenticatoms.com",
    "description": "We build AI chatbots, AI-powered websites, AI voice agents, AI receptionists, and AI sales agents for small and mid-sized businesses.",
    "serviceType": ["AI Chatbot Development", "AI Website Building", "AI Voice Agent Development", "AI Receptionist Solutions", "AI Sales Agent Automation", "Workflow Automation"],
    "areaServed": "Worldwide",
    "priceRange": "$1,500 - $5,000+",
    "telephone": "+1-227-241-6372",
    "email": "connect@agenticatoms.com"
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Do I need to understand AI or have a tech team to get an AI chatbot or AI website?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. You describe the problem. We handle every technical detail — the platforms, the integrations, the testing, and the deployment. We build AI chatbots, AI websites, AI voice agents, and AI sales agents for you."
        }
      },
      {
        "@type": "Question",
        "name": "How much does it cost to build an AI chatbot or AI voice agent?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most local business AI solutions range from $1,500 for a focused implementation to $5,000+ for a full end-to-end build. This includes AI chatbots, AI voice agents, AI receptionists, and custom AI apps. We give you a clear, fixed-price quote before any work begins."
        }
      },
      {
        "@type": "Question",
        "name": "How long does it take to build and deploy an AI chatbot or AI website?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most clients see results within the first 2-4 weeks of their AI solution going live. AI chatbots capture leads from day one. AI voice agents and receptionists start handling calls immediately after deployment."
        }
      },
      {
        "@type": "Question",
        "name": "What kinds of AI solutions do you build?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We build AI chatbots for websites, AI-powered websites, AI voice agents, AI receptionists, AI sales agents, automated follow-up workflows, appointment booking systems, AI content pipelines, review management, reporting dashboards, and custom integrations."
        }
      }
    ]
  }
];

const HomePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        path="/"
        description="Agentic Atoms builds custom AI chatbots, AI-powered websites, AI voice agents, AI receptionists, and AI sales agents for local businesses. We design, build, and deploy working AI systems in 30 days. Book a free discovery call."
        keywords="AI chatbot builder, AI website builder, build AI chatbot for website, AI voice agent, AI receptionist, AI sales agent, custom AI apps, AI chatbot for local business, AI automation, AI consulting firm, AI app development, AI customer service chatbot, AI appointment booking, AI lead generation chatbot, workflow automation"
        jsonLd={homepageJsonLd}
      />
      <Navbar />
      <HeroSection />
      <ServicesSection />
      <MarqueeBand items={['AI Chatbots', 'AI Voice Agents', 'AI Receptionists', 'AI Sales Agents', 'AI Websites', 'Workflow Automation']} speed={45} />
      <HowItWorksSection />
      <IndustriesSection />
      <TestimonialsSection />
      <AboutSection />
      <FAQSection />
      <ContactSection />
      <Footer />
    </div>
  );
};

const PageLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      {children}
      <Footer />
    </div>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Chat widget wrapper - hide on admin page
const ChatWidgetWrapper = () => {
  const location = useLocation();
  if (location.pathname === '/admin') return null;
  return <ChatWidget calendlyLink="https://calendly.com/agenticatoms/30min" />;
};

function App() {
  return (
    <HelmetProvider>
    <div className="App">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <SmoothScroll />
        <ScrollToTop />
        <AnimatedRoutes />
        <ChatWidgetWrapper />
        <CookieConsent />
      </BrowserRouter>
    </div>
    </HelmetProvider>
  );
}

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><HomePage /></PageTransition>} />
        <Route path="/careers" element={<PageTransition><PageLayout><CareersPage /></PageLayout></PageTransition>} />
        <Route path="/resources" element={<PageTransition><PageLayout><ResourcesPage /></PageLayout></PageTransition>} />
        <Route path="/privacy" element={<PageTransition><PageLayout><PrivacyPage /></PageLayout></PageTransition>} />
        <Route path="/terms" element={<PageTransition><PageLayout><TermsPage /></PageLayout></PageTransition>} />
        <Route path="/cookies" element={<PageTransition><PageLayout><CookiesPage /></PageLayout></PageTransition>} />
        <Route path="/accessibility" element={<PageTransition><PageLayout><AccessibilityPage /></PageLayout></PageTransition>} />
        <Route path="/industries/:slug" element={<PageTransition><PageLayout><IndustryPage /></PageLayout></PageTransition>} />
        <Route path="/blog" element={<PageTransition><PageLayout><BlogPage /></PageLayout></PageTransition>} />
        <Route path="/blog/:slug" element={<PageTransition><PageLayout><BlogPostPage /></PageLayout></PageTransition>} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
    </AnimatePresence>
  );
};

export default App;
